import { FC, SyntheticEvent, useEffect, useState } from 'react';
import { analytics, organization, signInWithEmailPassword } from 'firebase-service';
import styles from './login.module.scss';
import { FirebaseError } from 'firebase/app';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'hooks/SettingsContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'hooks/AuthContext/authContext.hook';
import { logEvent } from 'firebase/analytics';
import { getAccessToken } from 'helpers/getToken';

const Login: FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [hasLoginErrors, setHasLoginErrors] = useState(false);
    const [hasTokenError, setHasTokenError] = useState(false);
    const navigate = useNavigate();
    const { settings } = useSettings();
    const { t } = useTranslation();
    const { authUser } = useAuth();

    useEffect(() => {
        setHasLoginErrors(false);
    }, [username, password]);

    useEffect(() => {
        if (!settings || !authUser) return;
        sessionStorage.setItem('wd_id', settings.webDocClientId);
        sessionStorage.setItem('wd_secret', settings.webDocClientSecret);

        getAccessToken()
            .then((res) => {
                if (res.access_token) {
                    setHasTokenError(false);
                    navigate('/screensaver');
                    return;
                }
                if (res.error) {
                    setHasTokenError(true);
                }
                return;
            })
            .catch((err) => err);
    }, [settings, authUser, navigate]);

    const validateEmail = (email: string) => {
        return email.match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
    };

    const signIn = async (e: SyntheticEvent) => {
        const userEmail = `${username}@${organization}`;
        e.preventDefault();
        if (!validateEmail(userEmail)) {
            setHasLoginErrors(true);
            return;
        }

        try {
            const userCred = await signInWithEmailPassword(userEmail, password);
            logEvent(analytics, 'login', userCred);
        } catch (error) {
            if (error instanceof FirebaseError) {
                if (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
                    setHasLoginErrors(true);
                    return;
                }
                return;
            }
            setHasLoginErrors(true);
        }
    };

    return (
        <div className={styles.wrapper}>
            <form className={styles.form} onSubmit={signIn}>
                <label className={styles.formLabel} htmlFor="username">
                    {t('login_form_username')}
                </label>
                <input
                    id="username"
                    type="text"
                    className={styles.formField}
                    placeholder={t('login_form_username')}
                    onChange={(e) => setUsername(e.target.value)}
                />
                <label className={styles.formLabel} htmlFor="password">
                    {t('login_form_password')}
                </label>
                <input
                    type="password"
                    className={styles.formField}
                    placeholder={t('login_form_password')}
                    onChange={(e) => setPassword(e.target.value)}
                />
                <button
                    className={styles.submitButton}
                    type="submit"
                    disabled={!username || !password}
                >
                    {t('login_button')}
                </button>
                <div className={styles.error}>
                    {hasLoginErrors ? t('login_error') : hasTokenError && t('login_token_error')}
                </div>
            </form>
        </div>
    );
};

export default Login;
